import { supabase } from "./supabase";

export interface Goals {
  calories: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
  /** Daily fiber target in grams. Defaults to 30 — between the IOM's
   *  25g/women and 38g/men recommendations. Added 2026-05-26. */
  fiber_g_goal?: number;
  /** When true, dashboard + meal rows display "Net carbs" (= carbs - fiber)
   *  instead of total carbs. Carb goal interpretation changes but the
   *  number stays the same — common keto/low-carb framing. */
  track_net_carbs?: boolean;
  /** Whether adaptive macros are enabled (defaults true via DB column). */
  adapt_enabled?: boolean;
  /** Original calorie target from onboarding, untouched by adaptation. */
  baseline_calories?: number | null;
  /** Last time the adaptation algorithm ran. */
  last_adapted_at?: string | null;
  /** Human-readable summary of the last adaptation. */
  last_adapt_reason?: string | null;
}

/** Result of the adapt_calories() RPC — see migration for shape details. */
export interface AdaptResult {
  applied: boolean;
  reason?: string;
  old_calories?: number;
  new_calories?: number;
  pct_adjust?: number;
  actual_per_week?: number;
  goal_per_week?: number;
  meta_goal?: "lose" | "gain" | "maintain";
}

/**
 * Run the adaptation check. Cheap server-side function — call on Today tab
 * load (it short-circuits internally if it's been <6 days since the last
 * adaptation). Returns whether it changed anything.
 */
export async function runAdaptiveMacros(): Promise<AdaptResult | null> {
  if (!supabase) return null;
  const {
    data: { user }
  } = await supabase.auth.getUser();
  if (!user) return null;
  const { data, error } = await supabase.rpc("adapt_calories", { p_user_id: user.id });
  if (error) {
    console.warn("adapt_calories failed:", error.message);
    return null;
  }
  return data as AdaptResult;
}

/** Toggle adaptive macros on/off. */
export async function setAdaptEnabled(enabled: boolean): Promise<void> {
  if (!supabase) throw new Error("Supabase not configured");
  const {
    data: { user }
  } = await supabase.auth.getUser();
  if (!user) throw new Error("Not signed in");
  const { error } = await supabase
    .from("goals")
    .update({ adapt_enabled: enabled })
    .eq("user_id", user.id);
  if (error) throw error;
}

export const DEFAULT_GOALS: Goals = {
  calories: 2000,
  protein_g: 150,
  carbs_g: 200,
  fat_g: 65,
  fiber_g_goal: 30,
  track_net_carbs: false
};

// Default split of daily calories across the four meal types. Approximates the
// USDA's typical distribution. Could become user-configurable later.
export const MEAL_TYPE_WEIGHTS = {
  breakfast: 0.25,
  lunch: 0.35,
  dinner: 0.3,
  snack: 0.1
} as const;

export function targetKcalForMealType(
  dailyGoal: number,
  mealType: keyof typeof MEAL_TYPE_WEIGHTS
): number {
  return Math.round(dailyGoal * MEAL_TYPE_WEIGHTS[mealType]);
}

// Macro → calorie conversion (well-established).
export const KCAL_PER_G = { protein: 4, carbs: 4, fat: 9 };

export interface MacroRatio {
  p: number; // % of calories from protein
  c: number;
  f: number;
}

export interface Preset {
  name: string;
  description: string;
  ratio: MacroRatio;
}

export const PRESETS: Preset[] = [
  { name: "Balanced", description: "30 / 40 / 30", ratio: { p: 30, c: 40, f: 30 } },
  { name: "High protein", description: "40 / 30 / 30", ratio: { p: 40, c: 30, f: 30 } },
  { name: "Low carb", description: "30 / 15 / 55", ratio: { p: 30, c: 15, f: 55 } },
  { name: "Keto", description: "25 / 5 / 70", ratio: { p: 25, c: 5, f: 70 } }
];

export function caloriesFromMacros(g: Pick<Goals, "protein_g" | "carbs_g" | "fat_g">): number {
  return Math.round(
    g.protein_g * KCAL_PER_G.protein +
      g.carbs_g * KCAL_PER_G.carbs +
      g.fat_g * KCAL_PER_G.fat
  );
}

export function ratioFromGoals(g: Goals): MacroRatio {
  const total =
    g.protein_g * KCAL_PER_G.protein + g.carbs_g * KCAL_PER_G.carbs + g.fat_g * KCAL_PER_G.fat;
  if (total === 0) return { p: 33, c: 34, f: 33 };
  const p = Math.round(((g.protein_g * KCAL_PER_G.protein) / total) * 100);
  const c = Math.round(((g.carbs_g * KCAL_PER_G.carbs) / total) * 100);
  const f = 100 - p - c;
  return { p, c, f };
}

export function goalsFromCaloriesAndRatio(calories: number, ratio: MacroRatio): Goals {
  const pCal = calories * (ratio.p / 100);
  const cCal = calories * (ratio.c / 100);
  const fCal = calories * (ratio.f / 100);
  return {
    calories,
    protein_g: Math.round(pCal / KCAL_PER_G.protein),
    carbs_g: Math.round(cCal / KCAL_PER_G.carbs),
    fat_g: Math.round(fCal / KCAL_PER_G.fat)
  };
}

export async function loadGoalsRemote(): Promise<Goals> {
  if (!supabase) return DEFAULT_GOALS;
  const { data: userData } = await supabase.auth.getUser();
  const user = userData.user;
  if (!user) return DEFAULT_GOALS;

  const { data, error } = await supabase
    .from("goals")
    .select("calories, protein_g, carbs_g, fat_g, fiber_g_goal, track_net_carbs")
    .eq("user_id", user.id)
    .maybeSingle();
  if (error) {
    // Graceful fallback: if the new columns don't exist yet (migration not
    // applied), retry without them rather than blowing up the dashboard.
    if (/fiber_g_goal|track_net_carbs/.test(error.message)) {
      const retry = await supabase
        .from("goals")
        .select("calories, protein_g, carbs_g, fat_g")
        .eq("user_id", user.id)
        .maybeSingle();
      if (retry.error) throw retry.error;
      if (!retry.data) return DEFAULT_GOALS;
      return { ...DEFAULT_GOALS, ...retry.data };
    }
    throw error;
  }
  if (!data) return DEFAULT_GOALS;
  return { ...DEFAULT_GOALS, ...data };
}

export async function saveGoalsRemote(g: Goals): Promise<void> {
  if (!supabase) throw new Error("Supabase not configured");
  const { data: userData } = await supabase.auth.getUser();
  const user = userData.user;
  if (!user) throw new Error("Not signed in");
  // Only include columns we know exist. fiber_g_goal + track_net_carbs are
  // optional client-side, and if the migration hasn't been applied yet the
  // upsert would fail with an unknown-column error. Try the full upsert
  // first; on column-missing error retry with the core columns only.
  const fullPayload: Record<string, unknown> = {
    user_id: user.id,
    calories: g.calories,
    protein_g: g.protein_g,
    carbs_g: g.carbs_g,
    fat_g: g.fat_g,
    updated_at: new Date().toISOString()
  };
  if (g.fiber_g_goal !== undefined) fullPayload.fiber_g_goal = g.fiber_g_goal;
  if (g.track_net_carbs !== undefined) fullPayload.track_net_carbs = g.track_net_carbs;

  const { error } = await supabase.from("goals").upsert(fullPayload);
  if (error) {
    if (/fiber_g_goal|track_net_carbs/.test(error.message)) {
      // Migration not applied yet — drop the new fields and retry.
      delete fullPayload.fiber_g_goal;
      delete fullPayload.track_net_carbs;
      const retry = await supabase.from("goals").upsert(fullPayload);
      if (retry.error) throw retry.error;
      return;
    }
    throw error;
  }
}
